import { useEffect, useState } from "react";
import { Upload, FileText, CheckCircle2 } from "lucide-react";
import { AdminLayout } from "@/components/AdminLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { api, formatApiErrorDetail } from "@/lib/api";
import { RECEIPTS_BATCH } from "@/constants/testIds";
import { toast } from "sonner";

export default function ReceiptsBatch() {
  const [employees, setEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(true);
  const [periodLabel, setPeriodLabel] = useState("");
  const [issueDate, setIssueDate] = useState("");
  const [filesByEmployee, setFilesByEmployee] = useState({});
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(null);
  const [results, setResults] = useState(null);

  useEffect(() => {
    api.get("/employees")
      .then(({ data }) => setEmployees(data.filter((e) => e.status !== "baja")))
      .finally(() => setLoadingEmployees(false));
  }, []);

  const selectedCount = Object.values(filesByEmployee).filter(Boolean).length;
  const canUpload = periodLabel && issueDate && selectedCount > 0 && !uploading;

  const handleFileChange = (employeeId, file) => {
    setFilesByEmployee((prev) => ({ ...prev, [employeeId]: file || undefined }));
  };

  const handleUploadAll = async () => {
    const entries = Object.entries(filesByEmployee).filter(([, f]) => f);
    setUploading(true);
    setResults(null);
    setProgress({ done: 0, total: entries.length });
    let success = 0;
    let failed = 0;
    for (const [employeeId, file] of entries) {
      try {
        const formData = new FormData();
        formData.append("period_label", periodLabel);
        formData.append("issue_date", issueDate);
        formData.append("file", file);
        await api.post(`/employees/${employeeId}/receipts`, formData, { headers: { "Content-Type": "multipart/form-data" } });
        success++;
      } catch (err) {
        failed++;
      }
      setProgress((prev) => ({ ...prev, done: prev.done + 1 }));
    }
    setUploading(false);
    setResults({ success, failed });
    setFilesByEmployee({});
    if (failed === 0) toast.success(`${success} recibo(s) subido(s) correctamente`);
    else toast.error(`${success} subido(s), ${failed} con error`);
  };

  return (
    <AdminLayout>
      <div className="max-w-3xl">
        <h1 className="font-heading text-3xl font-bold text-charcoal tracking-tight">Recibos en lote</h1>
        <p className="text-charcoal/60 mt-1">Sube el recibo de un mismo periodo para varios empleados a la vez.</p>

        <div className="bg-card border border-petroleum/10 rounded-2xl p-5 mt-6 grid sm:grid-cols-2 gap-4">
          <div>
            <Label>Periodo (aplica a todos)</Label>
            <Input data-testid={RECEIPTS_BATCH.periodInput} value={periodLabel} onChange={(e) => setPeriodLabel(e.target.value)} placeholder="Ej. Febrero 2026 (1a quincena)" />
          </div>
          <div>
            <Label>Fecha de emisión (aplica a todos)</Label>
            <Input data-testid={RECEIPTS_BATCH.issueDateInput} type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} />
          </div>
        </div>

        <p className="text-sm font-semibold text-charcoal mt-6 mb-3">Selecciona un archivo por empleado ({selectedCount} listo(s))</p>
        <div className="space-y-2">
          {employees.map((e) => {
            const file = filesByEmployee[e.id];
            return (
              <div key={e.id} data-testid={RECEIPTS_BATCH.employeeRow} className="bg-card border border-petroleum/10 rounded-xl p-4 flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3">
                  <FileText className="w-4 h-4 text-petroleum shrink-0" strokeWidth={1.5} />
                  <div>
                    <p className="text-sm font-medium text-charcoal">{e.full_name}</p>
                    <p className="text-xs text-charcoal/50">{e.position}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {file && <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />}
                  <Input
                    data-testid={`receipts-batch-file-input-${e.id}`}
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    disabled={uploading}
                    onChange={(ev) => handleFileChange(e.id, ev.target.files?.[0])}
                    className="text-xs max-w-[220px]"
                  />
                </div>
              </div>
            );
          })}
          {loadingEmployees && <p className="text-charcoal/50 text-sm">Cargando empleados...</p>}
          {!loadingEmployees && employees.length === 0 && <p className="text-charcoal/50 text-sm">Sin empleados activos.</p>}
        </div>

        {progress && uploading && (
          <div data-testid={RECEIPTS_BATCH.progressBanner} className="mt-4 bg-accent/40 border border-petroleum/10 rounded-xl p-4 text-sm text-charcoal/70">
            Subiendo {progress.done} de {progress.total}...
          </div>
        )}

        {results && (
          <div data-testid={RECEIPTS_BATCH.resultBanner} className="mt-4 bg-card border border-petroleum/10 rounded-xl p-4 flex items-center gap-3">
            <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
            <p className="text-sm text-charcoal/70">{results.success} recibo(s) subido(s) correctamente{results.failed > 0 && <span className="text-red-600"> · {results.failed} con error</span>}</p>
          </div>
        )}

        <Button
          data-testid={RECEIPTS_BATCH.uploadAllButton}
          disabled={!canUpload}
          onClick={handleUploadAll}
          className="w-full sm:w-auto mt-6 bg-terracotta hover:bg-terracotta/90 rounded-full"
        >
          <Upload className="w-4 h-4 mr-2" /> {uploading ? "Subiendo..." : `Subir todos (${selectedCount})`}
        </Button>
      </div>
    </AdminLayout>
  );
}
